import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import WelcomeModal from "@/components/WelcomeModal";
import TrackerWidget from "@/components/TrackerWidget";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SKIN_LABEL = { dry_sensitive: "Dry / Sensitive", normal_combo: "Normal / Combination", oily: "Oily" };
const PHASE_LABEL = {
  skin_resilience: "Skin Resilience",
  making_actives_count: "Making Actives Count",
  precision_corrections: "Precision Corrections",
};
const VERDICT_CLASS = { green: "verdict-green", yellow: "verdict-yellow", red: "verdict-red" };

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [protocol, setProtocol] = useState(null);
  const [products, setProducts] = useState([]);
  const [showOriginal, setShowOriginal] = useState(false);

  const loadProtocol = (original) =>
    api.get(`/protocol${original ? "?original=true" : ""}`).then((r) => setProtocol(r.data)).catch(() => {});

  // Re-fetch latest user state on mount so we don't show stale phase
  useEffect(() => {
    refresh().catch(() => {});
    loadProtocol(false);
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOriginal = (val) => {
    if (val === showOriginal) return;
    setShowOriginal(val);
    loadProtocol(val);
  };

  const resetSwap = async (slotKey) => {
    try {
      await api.delete(`/swaps/${encodeURIComponent(slotKey)}`);
      loadProtocol(showOriginal);
    } catch (e) {
      // no-op
    }
  };

  const phase = user?.current_phase;
  const phaseCopy = phase === "making_actives_count"
    ? "You're building on your strengthened barrier — vitamin C in the AM, your retinoid in the PM, plus peptides for collagen support."
    : phase === "precision_corrections"
    ? "You've optimized your retinoid and are now layering in targeted treatments for specific concerns."
    : "You're calming, hydrating, and rebuilding barrier resilience. In the next phase, you'll work in transformative actives.";

  return (
    <div className="App" data-testid="page-dashboard">
      <NavBar />
      <WelcomeModal />
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="aa-eyebrow mb-3">Hello, {user?.name || "friend"}</div>
            <h1 className="font-display text-4xl lg:text-5xl leading-tight">Your skin, in <span className="serif-italic">focus</span>.</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/analyze" data-testid="dashboard-cta-analyze" className="aa-cta-primary">Analyze a product →</Link>
            <Link to="/protocol" data-testid="dashboard-cta-protocol" className="aa-cta-secondary">View my protocol</Link>
            <Link to="/summary" data-testid="dashboard-cta-summary" className="aa-cta-secondary">Print summary</Link>
            <Link to="/application-tips" data-testid="dashboard-cta-tips" className="aa-cta-secondary">Application tips</Link>
            <Link to="/how-it-works" data-testid="dashboard-cta-how" className="aa-cta-secondary">The SMART Method</Link>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-10">
          <div className={`aa-card ${phase === "skin_resilience" ? "aa-card-warm" : phase === "making_actives_count" ? "aa-card-sage" : "aa-card-blush"} p-6 lg:col-span-2`}>
            <div className="aa-eyebrow mb-2">Current phase</div>
            <div className="font-display text-2xl" data-testid="dashboard-current-phase">{PHASE_LABEL[phase] || "—"}</div>
            <p className="mt-2 text-[var(--aa-text-2)] text-sm">{phaseCopy}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {phase === "skin_resilience" && (
                <Link data-testid="dashboard-readiness" to="/readiness" className="aa-cta-secondary">
                  Am I ready for actives? →
                </Link>
              )}
              {phase === "making_actives_count" && (
                <>
                  <Link data-testid="dashboard-retinoid" to="/retinoid-augmentation" className="aa-cta-secondary">
                    Ready to level up my retinoid? →
                  </Link>
                  <Link data-testid="dashboard-precision" to="/precision-corrections" className="aa-cta-secondary">
                    Precision Corrections →
                  </Link>
                </>
              )}
              {phase === "precision_corrections" && (
                <Link data-testid="dashboard-precision" to="/precision-corrections" className="aa-cta-secondary">
                  Manage corrections →
                </Link>
              )}
              <Link data-testid="dashboard-readiness-revert" to="/readiness" className="aa-eyebrow underline underline-offset-4 self-center hover:text-[var(--aa-text)]">
                Change phase
              </Link>
            </div>
          </div>

          <div className="aa-card p-6">
            <div className="aa-eyebrow mb-2">Your skin</div>
            <div className="font-display text-2xl">{SKIN_LABEL[user?.skin_type] || "—"}</div>
            <div className="mt-2 text-sm text-[var(--aa-text-2)]">
              {user?.variation === "none" || !user?.variation ? "No special tendency" : user?.variation === "acne_prone" ? "Acne-prone" : "Rosacea / redness-prone"}
            </div>
            <Link data-testid="dashboard-edit-profile" to="/onboarding" className="aa-cta-secondary mt-5 inline-flex">Update</Link>
          </div>
        </div>

        <div className="mt-6">
          <TrackerWidget />
        </div>

        {protocol === null && (
          <div className="mt-8 aa-eyebrow" data-testid="dashboard-protocol-loading">Loading your protocol…</div>
        )}

        {protocol && (
          <div className="mt-8">
            {(protocol.has_swaps || showOriginal) && (
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="aa-eyebrow">Viewing:</span>
                <button
                  data-testid="dashboard-toggle-mine"
                  onClick={() => setOriginal(false)}
                  className={`aa-pill px-4 py-2 text-sm ${showOriginal ? "hover:bg-[var(--aa-elevated)]" : "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]"}`}
                >My version</button>
                <button
                  data-testid="dashboard-toggle-rey"
                  onClick={() => setOriginal(true)}
                  className={`aa-pill px-4 py-2 text-sm ${showOriginal ? "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]" : "hover:bg-[var(--aa-elevated)]"}`}
                >Original Dr Rey protocol</button>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              <RoutineCard title="Morning" steps={protocol.am} testid="dashboard-am" onReset={resetSwap} viewingOriginal={showOriginal} />
              <RoutineCard title="Evening" steps={protocol.pm} testid="dashboard-pm" onReset={resetSwap} viewingOriginal={showOriginal} />
            </div>
          </div>
        )}

        <div className="mt-12">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-3xl">Recently analyzed</h2>
            <Link to="/library" data-testid="dashboard-view-library" className="aa-eyebrow hover:text-[var(--aa-text)]">View all →</Link>
          </div>
          {products.length === 0 ? (
            <div className="aa-card p-8 mt-5 text-center text-[var(--aa-text-2)]">
              You haven't analyzed any products yet.{" "}
              <Link to="/analyze" data-testid="dashboard-empty-cta" className="underline underline-offset-4 text-[var(--aa-text)]">Start with your first one</Link>.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
              {products.slice(0, 6).map((p) => (
                <Link to={`/products/${p.id}`} key={p.id} data-testid={`product-card-${p.id}`} className="aa-card p-5 hover:bg-[var(--aa-bg-2)] transition-colors duration-300 block">
                  <span className={`aa-pill ${VERDICT_CLASS[p.verdict]}`}>{p.verdict.toUpperCase()}</span>
                  <div className="font-display text-lg mt-3">{p.product_name}</div>
                  <div className="text-sm text-[var(--aa-text-3)] mt-1">{p.category.replace("_", " ")}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ProductLine({ s, onReset, viewingOriginal }) {
  return (
    <div className="flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-sm text-[var(--aa-text-3)]">{s.step}</div>
        {s.swapped && !viewingOriginal && (
          <span className="aa-pill verdict-green text-xs px-2 py-0.5">Swapped</span>
        )}
        {s.precision && !viewingOriginal && (
          <span className="aa-pill verdict-yellow text-xs px-2 py-0.5">Precision Correction</span>
        )}
      </div>
      {s.url ? (
        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[var(--aa-text)] hover:underline underline-offset-4">{s.product}</a>
      ) : (
        <div className="text-[var(--aa-text)]">{s.product}</div>
      )}
      {s.swapped && !viewingOriginal && (
        <div className="text-sm text-[var(--aa-text-3)] mt-1 leading-relaxed">
          Replaces Dr Rey's: <span className="italic">{s.swap_original_product}</span>
          {onReset && (
            <button
              data-testid={`reset-swap-${s.swap_slot_key}`}
              onClick={() => onReset(s.swap_slot_key)}
              className="ml-2 underline underline-offset-2 hover:text-[var(--aa-text)]"
            >Reset</button>
          )}
        </div>
      )}
      {s.alt && (
        <div className="text-sm text-[var(--aa-text-2)] mt-1 leading-relaxed">
          {s.alt_label || "Alternative"}: {s.alt_url ? <a href={s.alt_url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{s.alt}</a> : s.alt}
        </div>
      )}
      {s.note && <div className="text-sm text-[var(--aa-text-3)] mt-1 italic leading-relaxed">{s.note}</div>}
    </div>
  );
}

function RoutineCard({ title, steps, testid, onReset, viewingOriginal }) {
  const tint = title === "Morning" ? "aa-card-warm" : "aa-card-stone";
  return (
    <div className={`aa-card ${tint} p-6`} data-testid={testid}>
      <div className="aa-eyebrow mb-3">{title}</div>
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 items-start">
            <span className="aa-eyebrow w-6 mt-1">{String(i + 1).padStart(2, "0")}</span>
            <ProductLine s={s} onReset={onReset} viewingOriginal={viewingOriginal} />
          </li>
        ))}
      </ol>
    </div>
  );
}
